/**
 * AddLead — /#/leads/new — Step 1 of the Lead-to-AE wizard.
 *
 * P0-5: Restored standard MQL → BDR → SQL → AE funnel.
 *   1. Add lead   ← this screen
 *   2. Route to BDR    (/#/leads/:id/route-bdr)
 *   3. BDR qualify     (/#/leads/:id/qualify)
 *   4. Assign to AE    (/#/leads/:id/assign)
 *   5. Done            (/#/leads/:id/success)
 *
 * Fields added per stress-agent Charlie: email, phone, region/territory.
 *
 * The Source dropdown can be linked to a marketing campaign / hook
 * from /#/marketing/hooks; the chosen event id is persisted on the
 * prospect as `marketing_event_id`.
 *
 * On save → POST /api/prospects → navigate to /#/leads/:id/route-bdr.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { api, getSession } from "@/lib/session";
import { RibbonProgress } from "./RibbonProgress";
import { loadEvents, type MarketingEvent } from "@/lib/marketing-hook-generator";

// ─── Lead wizard draft persistence ─────────────────────────────────────────────────
const LEAD_DRAFT_PREFIX = "aip:draft:lead-new";

interface LeadDraft {
  company: string;
  contactName: string;
  title: string;
  email: string;
  phone: string;
  region: string;
  industry: string;
  sourceKey: string;
  marketingEventId: string;
  notes: string;
  overrideSkipBdr: boolean;
  savedAt: string;
}

function getLeadUserKey(): string {
  const s = getSession();
  return s?.email?.toLowerCase() || s?.id || "anon";
}

function leadDraftStorageKey(userKey: string) {
  return `${LEAD_DRAFT_PREFIX}:${userKey}`;
}

function saveLeadDraft(userKey: string, d: LeadDraft) {
  try { localStorage.setItem(leadDraftStorageKey(userKey), JSON.stringify(d)); } catch { /* ignore */ }
}

function loadLeadDraft(userKey: string): LeadDraft | null {
  try {
    const raw = localStorage.getItem(leadDraftStorageKey(userKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LeadDraft;
    // Expire drafts older than 24 hours
    if (parsed.savedAt && Date.now() - new Date(parsed.savedAt).getTime() > 86_400_000) {
      localStorage.removeItem(leadDraftStorageKey(userKey));
      return null;
    }
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch { return null; }
}

function clearLeadDraft(userKey: string) {
  try { localStorage.removeItem(leadDraftStorageKey(userKey)); } catch { /* ignore */ }
}

function leadDraftHasContent(d: LeadDraft | null): boolean {
  if (!d) return false;
  return !!(d.company?.trim() || d.contactName?.trim() || d.notes?.trim());
}

// ─── Constants matching the seed data / production taxonomy ──────────────────

const INDUSTRIES = [
  "Industrial Manufacturing",
  "Automotive",
  "Aerospace & Defense",
  "Energy / Utilities",
  "Consumer Goods Manufacturing",
  "Building Products / Construction Materials",
  "Metals & Mining",
  "Chemicals & Plastics",
  "Food & Beverage Manufacturing",
];

const REGIONS = [
  "Northeast US",
  "Southeast US",
  "Midwest US",
  "Southwest US",
  "West US",
  "Canada East",
  "Canada West",
  "EMEA",
  "APAC",
  "LATAM",
  "Other",
];

// Base sources independent of marketing campaigns
const BASE_SOURCES = [
  { key: "marketing_mql",    label: "Inbound / MQL" },
  { key: "cold_outbound",    label: "Cold outbound (LinkedIn / email)" },
  { key: "event",            label: "Event / Trade Show / Webinar" },
  { key: "referral",         label: "Referral" },
  { key: "manager_assigned", label: "Manager assigned" },
];

const INPUT =
  "w-full rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-800 placeholder-neutral-400 focus:border-[#0B0B0F] focus:ring-2 focus:ring-neutral-200/60 outline-none transition-all duration-150";
const SELECT = `${INPUT} cursor-pointer`;
const LABEL =
  "block text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-1.5";
const REQUIRED_MARK = (
  <span className="text-[#DC2626] ml-0.5" aria-hidden>*</span>
);

// ─── Component ────────────────────────────────────────────────────────────────

export default function AddLead() {
  const [, navigate] = useLocation();
  const { toast } = useToast();

  // ── Draft hydration on mount ──────────────────────────────────────────────
  const userKey = useMemo(() => getLeadUserKey(), []);
  const existingDraft = useMemo(() => loadLeadDraft(userKey), [userKey]);
  const hasDraft = leadDraftHasContent(existingDraft);

  // Core fields
  const [company, setCompany] = useState(existingDraft?.company ?? "");
  const [contactName, setContactName] = useState(existingDraft?.contactName ?? "");
  const [title, setTitle] = useState(existingDraft?.title ?? "");
  const [email, setEmail] = useState(existingDraft?.email ?? "");
  const [phone, setPhone] = useState(existingDraft?.phone ?? "");
  const [region, setRegion] = useState(existingDraft?.region ?? "");
  const [industry, setIndustry] = useState(existingDraft?.industry ?? "Manufacturing");
  const [sourceKey, setSourceKey] = useState(existingDraft?.sourceKey ?? "marketing_mql");
  const [marketingEventId, setMarketingEventId] = useState<string>(existingDraft?.marketingEventId ?? "");
  const [notes, setNotes] = useState(existingDraft?.notes ?? "");
  const [showMore, setShowMore] = useState(false);
  const [overrideSkipBdr, setOverrideSkipBdr] = useState(existingDraft?.overrideSkipBdr ?? false);
  const [saving, setSaving] = useState(false);
  const [showDraftBanner, setShowDraftBanner] = useState(hasDraft);

  const [events, setEvents] = useState<MarketingEvent[]>([]);

  useEffect(() => {
    try { setEvents(loadEvents()); } catch { /* localStorage may be unavailable */ }
  }, []);

  // ── Refs for synchronous flush on unmount ────────────────────────────────
  const stateRef = useRef({ company, contactName, title, email, phone, region, industry, sourceKey, marketingEventId, notes, overrideSkipBdr });
  stateRef.current = { company, contactName, title, email, phone, region, industry, sourceKey, marketingEventId, notes, overrideSkipBdr };
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushLeadDraft = useCallback(() => {
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    const s = stateRef.current;
    if (s.company.trim() || s.contactName.trim() || s.notes.trim()) {
      saveLeadDraft(userKey, { ...s, savedAt: new Date().toISOString() });
    }
  }, [userKey]);

  // Debounced autosave (800 ms)
  useEffect(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(flushLeadDraft, 800);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, contactName, title, email, phone, region, industry, sourceKey, marketingEventId, notes, overrideSkipBdr, flushLeadDraft]);

  // Flush on unmount (hash back-navigation — PRIMARY FIX for P0-2)
  useEffect(() => {
    return () => {
      const s = stateRef.current;
      if (s.company.trim() || s.contactName.trim() || s.notes.trim()) flushLeadDraft();
    };
  }, [flushLeadDraft]);

  // Flush when tab goes hidden (pagehide / visibilitychange)
  useEffect(() => {
    const onHide = () => flushLeadDraft();
    const onVis = () => { if (document.visibilityState === "hidden") flushLeadDraft(); };
    window.addEventListener("pagehide", onHide);
    document.addEventListener("visibilitychange", onVis);
    return () => { window.removeEventListener("pagehide", onHide); document.removeEventListener("visibilitychange", onVis); };
  }, [flushLeadDraft]);

  // beforeunload warning
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (company.trim() || contactName.trim()) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [company, contactName]);

  const canSave = company.trim().length > 0 && contactName.trim().length > 0;

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const result = await api<{ id: number; company: string; full_name: string }>(
        "/api/prospects",
        {
          method: "POST",
          body: JSON.stringify({
            company: company.trim(),
            full_name: contactName.trim(),
            title: title.trim(),
            email: email.trim() || undefined,
            phone: phone.trim() || undefined,
            region: region || undefined,
            source: sourceKey,
            owner_role: overrideSkipBdr ? "ae" : "bdr",
            notes: notes.trim() || undefined,
            bdr_override: overrideSkipBdr,
            marketing_event_id: marketingEventId || null,
          }),
        }
      );

      // Clear the lead draft once submitted
      clearLeadDraft(userKey);

      // Log the override for auditability (visible in browser console + localStorage)
      if (overrideSkipBdr) {
        try {
          const log = JSON.parse(localStorage.getItem("aip:bdr-override-log") ?? "[]");
          log.push({
            prospect_id: result.id,
            company: result.company,
            at: new Date().toISOString(),
            reason: "AE self-sourced — explicit BDR skip override",
          });
          localStorage.setItem("aip:bdr-override-log", JSON.stringify(log));
        } catch { /* noop */ }
        // AE override path → skip directly to AE assignment
        navigate(`/leads/${result.id}/assign?override=1`);
      } else {
        // Standard path → Route to BDR step
        navigate(`/leads/${result.id}/route-bdr`);
      }
    } catch (e: any) {
      toast({
        title: "Could not save lead",
        description: e?.message ?? "Check your connection and try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }

  // Build source options: base sources + any approved marketing events as
  // "Campaign:" prefixed entries.
  const sourceOptions: { key: string; label: string; eventId?: string }[] = [
    ...BASE_SOURCES,
    ...events.map((ev) => ({
      key: `event:${ev.id}`,
      label: `Campaign — ${ev.name}`,
      eventId: ev.id,
    })),
  ];

  function handleSourceChange(value: string) {
    if (value.startsWith("event:")) {
      const id = value.slice("event:".length);
      setMarketingEventId(id);
      setSourceKey("event"); // back-end enum
    } else {
      setMarketingEventId("");
      setSourceKey(value);
    }
  }

  // Compose the displayed select value
  const selectValue = marketingEventId ? `event:${marketingEventId}` : sourceKey;

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      {/* Top nav bar */}
      <header className="sticky top-0 z-40 bg-white border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-5 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/dashboards/bdr")}
              className="p-1.5 rounded-md text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
              aria-label="Back to BDR dashboard"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
                <path d="M11 13.5L6.5 9 11 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 leading-none mb-0.5">Mi6 Attilius</div>
              <div className="text-sm font-bold text-[#0B0B0F] leading-none">Add Lead</div>
            </div>
          </div>
          <div className="text-[10px] text-neutral-500">Step 1 of 5</div>
        </div>
        <RibbonProgress step={1} />
      </header>

      {/* Form */}
      <main className="max-w-2xl mx-auto px-5 py-8">
        <div className="mb-7">
          <h1 className="text-[22px] font-bold text-[#0B0B0F] tracking-tight">New lead</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Fill in the basics. The lead will enter the BDR qualification queue.
          </p>
        </div>

        {/* Draft restore banner */}
        {showDraftBanner && (
          <div
            className="mb-5 flex items-start gap-3 rounded-xl border border-orange-400/40 bg-orange-50 px-4 py-3"
            data-testid="lead-draft-banner"
            role="status"
          >
            <svg className="mt-0.5 shrink-0 text-orange-500" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12Z" fill="currentColor" fillOpacity="0.2" />
              <path d="M8 5v4M8 11h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <div className="flex-1 text-sm text-yellow-800">
              <span className="font-semibold">Resume draft{company ? ` — "${company}"` : ""}?</span>
              <span className="ml-1 font-normal">Your previous entries were saved automatically.</span>
            </div>
            <button
              type="button"
              onClick={() => setShowDraftBanner(false)}
              className="text-xs font-semibold text-orange-700 hover:text-yellow-900 px-2 py-1 rounded hover:bg-orange-100"
              data-testid="lead-draft-resume-btn"
            >Resume</button>
            <button
              type="button"
              onClick={() => {
                clearLeadDraft(userKey);
                setCompany(""); setContactName(""); setTitle("");
                setEmail(""); setPhone(""); setRegion("");
                setIndustry("Manufacturing"); setSourceKey("marketing_mql");
                setMarketingEventId(""); setNotes(""); setOverrideSkipBdr(false);
                setShowDraftBanner(false);
              }}
              className="text-xs text-neutral-500 hover:text-neutral-700 px-2 py-1 rounded hover:bg-neutral-100"
              data-testid="lead-draft-discard-btn"
            >Discard</button>
          </div>
        )}

        <form onSubmit={handleSave} noValidate>
          <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-6 space-y-5">

            {/* Company name */}
            <div>
              <label className={LABEL}>Company name {REQUIRED_MARK}</label>
              <input
                className={INPUT}
                autoFocus
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Company name"
                required
                data-testid="lead-company"
              />
            </div>

            {/* Contact name */}
            <div>
              <label className={LABEL}>Contact name {REQUIRED_MARK}</label>
              <input
                className={INPUT}
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="First and last name"
                required
                data-testid="lead-contact"
              />
            </div>

            {/* Title */}
            <div>
              <label className={LABEL}>Title</label>
              <input
                className={INPUT}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Director of Operations"
                data-testid="lead-title"
              />
            </div>

            {/* Email + Phone — two columns */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={LABEL}>Email</label>
                <input
                  className={INPUT}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="contact@company.com"
                  data-testid="lead-email"
                />
              </div>
              <div>
                <label className={LABEL}>Phone</label>
                <input
                  className={INPUT}
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  data-testid="lead-phone"
                />
              </div>
            </div>

            {/* Industry + Region — two columns */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={LABEL}>Industry</label>
                <select
                  className={SELECT}
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  data-testid="lead-industry"
                >
                  {INDUSTRIES.map((ind) => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={LABEL}>Region / Territory</label>
                <select
                  className={SELECT}
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  data-testid="lead-region"
                >
                  <option value="">— Select region —</option>
                  {REGIONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Source */}
            <div>
              <label className={LABEL}>
                Source
                <a
                  href="#/marketing/hooks"
                  className="ml-1.5 font-normal normal-case tracking-normal text-[10px] text-neutral-400 hover:text-[#0B0B0F] underline"
                >
                  manage campaigns →
                </a>
              </label>
              <select
                className={SELECT}
                value={selectValue}
                onChange={(e) => handleSourceChange(e.target.value)}
                data-testid="lead-source"
              >
                <optgroup label="Channel">
                  {BASE_SOURCES.map((s) => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </optgroup>
                {events.length > 0 && (
                  <optgroup label="Marketing campaigns">
                    {events.map((ev) => (
                      <option key={ev.id} value={`event:${ev.id}`}>
                        {ev.name}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              {marketingEventId && (
                <p className="text-[10px] text-neutral-500 mt-1">
                  Linked to campaign · <code className="font-mono">{marketingEventId}</code>
                </p>
              )}
            </div>

            {/* More details toggle */}
            <div>
              <button
                type="button"
                onClick={() => setShowMore((v) => !v)}
                className="flex items-center gap-1.5 text-xs font-semibold text-neutral-500 hover:text-[#0B0B0F] transition-colors"
              >
                <svg
                  width="14" height="14" viewBox="0 0 14 14" fill="none"
                  className={`transition-transform duration-200 ${showMore ? "rotate-90" : ""}`}
                  aria-hidden
                >
                  <path d="M5 2.5L9.5 7 5 11.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {showMore ? "Hide additional details" : "Add notes / context"}
              </button>

              {showMore && (
                <div className="mt-4 space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
                  <div>
                    <label className={LABEL}>Notes</label>
                    <textarea
                      className={`${INPUT} resize-none`}
                      rows={3}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Initial context, how you found them, any known pain..."
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Override — skip BDR (AE self-sourced) */}
          <div className="mt-4 bg-white rounded-2xl border border-orange-200 shadow-sm p-5">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 w-4 h-4 accent-[#DC2626] cursor-pointer"
                checked={overrideSkipBdr}
                onChange={(e) => setOverrideSkipBdr(e.target.checked)}
                data-testid="override-skip-bdr"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-orange-700">
                    Override
                  </span>
                  <span className="text-sm font-semibold text-[#0B0B0F]">
                    Skip BDR — this is a self-sourced AE lead
                  </span>
                </div>
                <p className="text-[11px] text-neutral-500 mt-1 leading-relaxed">
                  Bypasses the BDR qualification queue and routes directly to AE
                  assignment. Use only when the AE has already qualified the lead
                  themselves. The override is logged on the prospect record.
                </p>
              </div>
            </label>
          </div>

          {/* Required field note */}
          <p className="text-[11px] text-neutral-500 mt-3 px-1">
            <span className="text-[#DC2626]">*</span> Required fields
          </p>

          {/* CTA */}
          <div className="mt-6 flex items-center gap-3">
            <button
              type="submit"
              disabled={!canSave || saving}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#0B0B0F] text-white text-sm font-bold tracking-wide transition-all duration-150 hover:bg-[#374151] disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
              data-testid="save-lead"
            >
              {saving ? (
                <>
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  {overrideSkipBdr ? "Save and assign to AE" : "Save and route to BDR"}
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate("/dashboards/bdr")}
              className="px-4 py-3 rounded-xl border border-neutral-200 text-sm font-semibold text-neutral-600 hover:bg-neutral-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
